import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  FolderIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Task } from '../../types';
import { tasksAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface UserTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onTaskUpdate?: (task: Task) => void;
}

const UserTaskModal: React.FC<UserTaskModalProps> = ({
  isOpen,
  onClose,
  task,
  onTaskUpdate,
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [taskStatus, setTaskStatus] = useState<'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE' | 'CANCELLED'>(task?.status || 'TODO');

  // Enhanced state for time tracking, feedback, and completion
  const [activeTab, setActiveTab] = useState<'details' | 'time' | 'feedback' | 'completion'>('details');
  const [actualHours, setActualHours] = useState(task?.actualHours || 0);
  const [progressPercentage, setProgressPercentage] = useState(task?.progressPercentage || 0);
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [newFeedback, setNewFeedback] = useState('');
  const [completionDetails, setCompletionDetails] = useState<any[]>([]);
  const [newCompletionDetail, setNewCompletionDetail] = useState({ description: '', notes: '' });
  const [showCompletionForm, setShowCompletionForm] = useState(false);

  useEffect(() => {
    if (task) {
      setTaskStatus(task.status);
      setActualHours(task.actualHours || 0);
      setProgressPercentage(task.progressPercentage || 0);
      loadTaskData();
    }
  }, [task]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTaskData = async () => {
    if (!task) return;

    try {
      const [feedbacksData, completionData] = await Promise.all([
        tasksAPI.getTaskFeedbacks(task.id),
        tasksAPI.getTaskFinishDetails(task.id),
      ]);
      setFeedbacks(feedbacksData);
      setCompletionDetails(completionData);
    } catch (error) {
      console.error('Error loading task data:', error);
    }
  };

  const handleStatusUpdate = async (newStatus: 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE' | 'CANCELLED') => {
    if (!task || !user) return;

    // Check if this is a recurring task
    if ((task as any).isRecurring || task.id.startsWith('recurring-')) {
      toast.error('Cannot update status of recurring task instances. Manage the recurring task template instead.');
      return;
    }

    try {
      setIsLoading(true);

      const statusData: any = {
        status: newStatus,
        userId: user.id,
      };

      // If marking as done and completion form is shown, include completion details
      if (newStatus === 'DONE' && showCompletionForm && newCompletionDetail.description.trim()) {
        statusData.completionDetails = {
          description: newCompletionDetail.description,
          notes: newCompletionDetail.notes,
        };
      }

      const updatedTask = await tasksAPI.updateTaskStatus(task.id, statusData);

      // Update local state immediately
      setTaskStatus(newStatus);

      // If marking as done, also update progress to 100%
      if (newStatus === 'DONE') {
        setProgressPercentage(100);
        // Update the task's progress in the backend
        try {
          await tasksAPI.updateTaskTime(task.id, {
            progressPercentage: 100,
            actualHours: actualHours || task.actualHours
          });
        } catch (error) {
          console.error('Error updating progress to 100%:', error);
        }
      }

      // Call the parent update handler with the updated task
      onTaskUpdate?.(updatedTask);

      // Reset completion form
      if (newStatus === 'DONE') {
        setNewCompletionDetail({ description: '', notes: '' });
        setShowCompletionForm(false);
        // Reload task data to get the latest state including completion details
        setTimeout(() => {
          loadTaskData();
        }, 100);
      }

      // Provide specific success messages
      const statusMessages = {
        'TODO': 'Task moved to To Do',
        'IN_PROGRESS': 'Task started',
        'REVIEW': 'Task moved to Review',
        'DONE': 'Task completed successfully! 🎉',
        'CANCELLED': 'Task cancelled'
      };

      toast.success(statusMessages[newStatus] || `Task status updated to ${newStatus.replace('_', ' ').toLowerCase()}`);
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeUpdate = async () => {
    if (!task || !user) return;

    try {
      setIsLoading(true);

      // Validate and clamp progress percentage between 0 and 100
      const clampedProgress = Math.max(0, Math.min(100, Math.round(progressPercentage)));

      const updatedTask = await tasksAPI.updateTaskTime(task.id, {
        actualHours,
        progressPercentage: clampedProgress,
      });

      // Update local state with clamped value
      setProgressPercentage(clampedProgress);

      onTaskUpdate?.(updatedTask);
      toast.success('Time tracking updated successfully');
    } catch (error) {
      console.error('Error updating time tracking:', error);
      toast.error('Failed to update time tracking');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFeedback = async () => {
    if (!task || !user || !newFeedback.trim()) return;

    try {
      setIsLoading(true);
      await tasksAPI.addTaskFeedback(task.id, {
        content: newFeedback,
        userId: user.id,
      });
      setNewFeedback('');
      loadTaskData();
      toast.success('Feedback added successfully');
    } catch (error) {
      console.error('Error adding feedback:', error);
      toast.error('Failed to add feedback');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCompletionDetail = async () => {
    if (!task || !user || !newCompletionDetail.description.trim()) return;

    try {
      setIsLoading(true);
      await tasksAPI.addTaskFinishDetail(task.id, {
        description: newCompletionDetail.description,
        notes: newCompletionDetail.notes,
        userId: user.id,
      });
      setNewCompletionDetail({ description: '', notes: '' });
      setShowCompletionForm(false);
      loadTaskData();
      toast.success('Completion detail added successfully');
    } catch (error) {
      console.error('Error adding completion detail:', error);
      toast.error('Failed to add completion detail');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'REVIEW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'DONE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'LOW':
        return 'bg-green-100 text-green-800';
      case 'NORMAL':
        return 'bg-blue-100 text-blue-800';
      case 'HIGH':
        return 'bg-yellow-100 text-yellow-800';
      case 'URGENT':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      case 'HIGH':
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date() && taskStatus !== 'DONE';
  };

  if (!task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {task.title}
                  </h3>
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(taskStatus)}`}>
                      {taskStatus.replace('_', ' ')}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                      <div className="flex items-center space-x-1">
                        {getPriorityIcon(task.priority)}
                        <span>{task.priority}</span>
                      </div>
                    </span>
                    {((task as any).isRecurring || task.id.startsWith('recurring-')) && (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="w-3 h-3" />
                          <span>RECURRING</span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Completion Celebration Banner */}
              {taskStatus === 'DONE' && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0, y: -20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200"
                >
                  <div className="flex items-center space-x-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, duration: 0.3, type: "spring" }}
                    >
                      <CheckCircleIcon className="w-8 h-8 text-green-500" />
                    </motion.div>
                    <div>
                      <h4 className="text-lg font-bold text-green-700">🎉 Task Completed!</h4>
                      <p className="text-sm text-green-600">Excellent work! This task has been marked as complete.</p>
                      {task?.updatedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Completed on {new Date(task.updatedAt).toLocaleDateString()} at {new Date(task.updatedAt).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Description */}
              {task.description && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {task.description}
                  </p>
                </div>
              )}

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { id: 'details', name: 'Details', icon: DocumentTextIcon },
                    { id: 'time', name: 'Time Tracking', icon: ClockIcon },
                    { id: 'feedback', name: 'Feedback', icon: ChatBubbleLeftRightIcon },
                    { id: 'completion', name: 'Completion', icon: CheckCircleIcon },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="mb-6">
                {activeTab === 'details' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Due Date */}
                    {task.dueDate && (
                      <div className="flex items-center space-x-3">
                        <CalendarIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Due Date</p>
                          <p className={`text-sm ${isOverdue(task.dueDate) ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            {formatDate(task.dueDate)}
                            {isOverdue(task.dueDate) && ' (Overdue)'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Created Date */}
                    <div className="flex items-center space-x-3">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Created</p>
                        <p className="text-sm text-gray-600">
                          {formatDate(task.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* Team */}
                    {task.team && (
                      <div className="flex items-center space-x-3">
                        <UserIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Team</p>
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: task.team.color }}
                            />
                            <p className="text-sm text-gray-600">{task.team.name}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Project */}
                    {task.project && (
                      <div className="flex items-center space-x-3">
                        <FolderIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Project</p>
                          <p className="text-sm text-gray-600">{task.project.name}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'time' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Estimated Hours (Set by Admin)
                        </label>
                        <input
                          type="number"
                          value={task?.estimatedHours || 0}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                          min="0"
                          step="0.5"
                        />
                        <p className="text-xs text-gray-500 mt-1">Only administrators can modify estimated hours</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Actual Hours
                        </label>
                        <input
                          type="number"
                          value={actualHours}
                          onChange={(e) => setActualHours(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          min="0"
                          step="0.5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Progress (%)
                        </label>
                        <input
                          type="number"
                          value={progressPercentage}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            const clampedValue = Math.max(0, Math.min(100, value));
                            setProgressPercentage(clampedValue);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          min="0"
                          max="100"
                        />
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{progressPercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </div>

                    {/* Time Summary */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Time Summary</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Estimated:</span>
                          <span className="ml-2 font-medium">{task?.estimatedHours || 0} hours</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Actual:</span>
                          <span className="ml-2 font-medium">{actualHours} hours</span>
                        </div>
                      </div>
                      {task?.estimatedHours && task.estimatedHours > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {actualHours > task.estimatedHours
                            ? `Over estimate by ${(actualHours - task.estimatedHours).toFixed(1)} hours`
                            : actualHours < task.estimatedHours
                            ? `Under estimate by ${(task.estimatedHours - actualHours).toFixed(1)} hours`
                            : 'On track with estimate'
                          }
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      {((task as any).isRecurring || task.id.startsWith('recurring-')) ? (
                        <div className="text-sm text-gray-500 italic">
                          Time tracking is not available for recurring task instances.
                        </div>
                      ) : (
                        <button
                          onClick={handleTimeUpdate}
                          disabled={isLoading}
                          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                        >
                          {isLoading ? 'Updating...' : 'Update Progress & Hours'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'feedback' && (
                  <div className="space-y-4">
                    {/* Add Feedback Form - Only for Admins and Managers */}
                    {user && (user.role === 'ADMIN' || user.role === 'MANAGER') && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Add Feedback</h4>
                        <div className="space-y-3">
                          <textarea
                            value={newFeedback}
                            onChange={(e) => setNewFeedback(e.target.value)}
                            placeholder="Share your thoughts, updates, or questions about this task..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            rows={3}
                          />
                          <div className="flex justify-end">
                            <button
                              onClick={handleAddFeedback}
                              disabled={isLoading || !newFeedback.trim()}
                              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                            >
                              {isLoading ? 'Adding...' : 'Add Feedback'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Message for regular users */}
                    {user && user.role === 'USER' && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-700">
                          💡 Only managers and administrators can add feedback to tasks.
                        </p>
                      </div>
                    )}

                    {/* Feedback List */}
                    <div className="space-y-3">
                      {feedbacks.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No feedback yet. Be the first to share your thoughts!
                        </p>
                      ) : (
                        feedbacks.map((feedback) => (
                          <div key={feedback.id} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {feedback.user.firstName} {feedback.user.lastName}
                                </span>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  feedback.user.role === 'ADMIN' ? 'bg-red-100 text-red-800' :
                                  feedback.user.role === 'MANAGER' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {feedback.user.role}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(feedback.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                              {feedback.content}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'completion' && (
                  <div className="space-y-4">
                    {/* Add Completion Detail Form */}
                    {(showCompletionForm || taskStatus === 'DONE') && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">
                          {taskStatus === 'DONE' ? 'Task Completion Details' : 'Add Completion Detail'}
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description *
                            </label>
                            <textarea
                              value={newCompletionDetail.description}
                              onChange={(e) => setNewCompletionDetail(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Describe what was completed, any challenges faced, or outcomes achieved..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              rows={3}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Additional Notes
                            </label>
                            <textarea
                              value={newCompletionDetail.notes}
                              onChange={(e) => setNewCompletionDetail(prev => ({ ...prev, notes: e.target.value }))}
                              placeholder="Any additional notes, lessons learned, or follow-up items..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              rows={2}
                            />
                          </div>
                          <div className="flex justify-end space-x-3">
                            {showCompletionForm && taskStatus !== 'DONE' && (
                              <button
                                onClick={() => setShowCompletionForm(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                              >
                                Cancel
                              </button>
                            )}
                            <button
                              onClick={handleAddCompletionDetail}
                              disabled={isLoading || !newCompletionDetail.description.trim()}
                              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                            >
                              {isLoading ? 'Adding...' : 'Add Completion Detail'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Completion Detail Button */}
                    {!showCompletionForm && taskStatus !== 'DONE' && (
                      <button
                        onClick={() => setShowCompletionForm(true)}
                        className="w-full px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="w-4 h-4 inline mr-2" />
                        Add Completion Detail
                      </button>
                    )}

                    {/* Completion Details List */}
                    <div className="space-y-3">
                      {completionDetails.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No completion details yet.
                        </p>
                      ) : (
                        completionDetails.map((detail) => (
                          <div key={detail.id} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-sm font-medium text-gray-900">
                                {detail.user.firstName} {detail.user.lastName}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(detail.completedAt)}
                              </span>
                            </div>
                            <div className="space-y-2">
                              <div>
                                <h5 className="text-sm font-medium text-gray-900">Description</h5>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {detail.description}
                                </p>
                              </div>
                              {detail.notes && (
                                <div>
                                  <h5 className="text-sm font-medium text-gray-900">Notes</h5>
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                    {detail.notes}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <div className="flex space-x-3">
                  {((task as any).isRecurring || task.id.startsWith('recurring-')) ? (
                    <div className="text-sm text-gray-500 italic">
                      Status changes are not available for recurring task instances. Manage the recurring task template instead.
                    </div>
                  ) : (
                    <>
                      {taskStatus === 'TODO' && (
                        <button
                          onClick={() => handleStatusUpdate('IN_PROGRESS')}
                          disabled={isLoading}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <PlayIcon className="w-4 h-4 mr-2" />
                          Start Task
                        </button>
                      )}

                      {taskStatus === 'IN_PROGRESS' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate('DONE')}
                            disabled={isLoading}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            <CheckCircleIcon className="w-4 h-4 mr-2" />
                            Complete Task
                          </button>
                          <button
                            onClick={() => {
                              setActiveTab('completion');
                              setShowCompletionForm(true);
                            }}
                            disabled={isLoading}
                            className="inline-flex items-center px-2 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <DocumentTextIcon className="w-4 h-4 mr-1" />
                            Add Details
                          </button>
                          <button
                            onClick={() => handleStatusUpdate('TODO')}
                            disabled={isLoading}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <PauseIcon className="w-4 h-4 mr-2" />
                            Pause
                          </button>
                        </>
                      )}

                      {taskStatus === 'DONE' && (
                        <button
                          onClick={() => handleStatusUpdate('IN_PROGRESS')}
                          disabled={isLoading}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <PlayIcon className="w-4 h-4 mr-2" />
                          Reopen
                        </button>
                      )}
                    </>
                  )}
                </div>

                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UserTaskModal;
